/* eslint-disable react/jsx-no-bind */
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Callout, Flex, Modal, Switch, Text } from '@homeexchange/design';

import i18n from '../../i18n';
import Utils from '../../utils/utils';
import User from '../../models/User';
import ApiHome from '../../api/Home';

const CollectionOnboardingTour = ({ user }) => {
    const [step, setStep] = useState(0);
    const [isErrorMessageDisplayed, setIsErrorMessageDisplayed] = useState(false);

    const slides = [
        {
            title: i18n.t('collection:collection-mbr-internal.welcome-tour.step2.title'),
            text: i18n.t('collection:collection-mbr-internal.welcome-tour.step2.content'),
            illustration: `${Utils.getAssetsDomain()}/images/website/collection/home-value.svg`
        },
        {
            title: i18n.t('collection:collection-mbr-internal.welcome-tour.step3.title'),
            text: i18n.t('collection:collection-mbr-internal.welcome-tour.step3.content'),
            illustration: `${Utils.getAssetsDomain()}/images/website/collection/collection-homes.svg`
        },
        {
            type: 'freeTrial',
            title: i18n.t('collection:collection-mbr-internal.welcome-tour.step4.title'),
            text: i18n.t('collection:collection-mbr-internal.welcome-tour.step4.content'),
            illustration: `${Utils.getAssetsDomain()}/images/website/collection/collection-unwanted-request.svg`,
            additionnalContent: ['switch']
        },
        {
            type: 'freeTrial',
            title: i18n.t('collection:collection-mbr-internal.welcome-tour.step4.title'),
            content: (
                <>
                    {isUserFreeTrial && (
                        <p
                            className="marg-text"
                            dangerouslySetInnerHTML={{
                                __html: i18n.t('collection:collection-trialist.welcome-modal.content')
                            }}
                        />
                    )}
                    <ul
                        dangerouslySetInnerHTML={{
                            __html: i18n.t('collection:collection-trialist.welcome-modal.new-benefits-list')
                        }}
                    ></ul>
                    {isUserFreeTrial && (
                        <p
                            dangerouslySetInnerHTML={{
                                __html: i18n.t('collection:collection-trialist.welcome-modal.gp-bonus', {
                                    GP_welcome_bonus: '1050'
                                })
                            }}
                        ></p>
                    )}
                </>
            ),
            illustration: `${Utils.getAssetsDomain()}/images/website/collection/end-of-free-trial.svg`
        }
    ];

    const isUserFreeTrial = user.attributes?.trial?.state === 'active';

    const addToFilteredState = () => {
        if (!isUserFreeTrial) {
            return slides;
        }

        return slides.filter((item) => item.type !== 'freeTrial');
    };

    const filteredSlides = addToFilteredState();

    const preloadImages = () => {
        filteredSlides.forEach((slide) => {
            const img = new Image();
            slide.illustration = img.src;
        });
    };

    const next = (currentStep) => {
        currentStep < filteredSlides.length - 1 && setStep(step + 1);
        if (currentStep >= filteredSlides.length - 1) {
            const urlSearchParams = new URLSearchParams(window.location.search);
            const redirectToParam = urlSearchParams.get('redirectTo');

            if (redirectToParam) {
                document.location.assign(redirectToParam);
            } else {
                document.location.assign(`/dashboard`);
            }
        }
    };

    const toggleVerifiedOnly = (checked) => {
        isErrorMessageDisplayed && setIsErrorMessageDisplayed(false);

        const data = { is_search_he_collection_only: checked };
        ApiHome.updateHomesFlag(data).catch((error) => {
            setIsErrorMessageDisplayed(true);
            const message = error.responseJSON.errors[0];
            return message;
        });
    };

    useEffect(() => {
        preloadImages();
        //toggleVerifiedOnly prop must be the same as default value in switch component
        toggleVerifiedOnly(true);
    }, []);

    /* eslint-disable react/no-multi-comp */

    return (
        filteredSlides && (
            <Modal
                title={filteredSlides[step].title}
                isOpen={true}
                confirmActionText={
                    step < filteredSlides.length - 1
                        ? i18n.t('collection:welcome-tour.next')
                        : i18n.t('collection:collection-trialist.welcome-modal.button')
                }
                onConfirmAction={() => next(step)}
                alternativeAction={i18n.t('collection:welcome-tour.back')}
                onAlternativeAction={() => setStep(step - 1)}
                isClosable={false}
                areDividersVisible={false}
                multiSteps={{ isVisible: true, currentStep: step, stepNumber: filteredSlides.length }}
            >
                <Flex justifyContent="center">
                    {filteredSlides[step].illustration && (
                        <img
                            className="modal-welcome-illustration"
                            src={filteredSlides[step].illustration}
                            alt=""
                        />
                    )}
                </Flex>
                <Flex isFullWidth={true}>
                    {filteredSlides[step].text && (
                        <p
                            className="marg-text"
                            dangerouslySetInnerHTML={{ __html: filteredSlides[step].text }}
                        />
                    )}
                    {filteredSlides[step].content && filteredSlides[step].content}
                    {filteredSlides[step].additionnalContent?.includes('switch') && (
                        <>
                            <div className="welcome-tour-content-container">
                                <Switch
                                    label={i18n.t(
                                        'collection:collection-mbr-internal.welcome-tour.step4.toggle-title'
                                    )}
                                    description={i18n.t(
                                        'collection:collection-mbr-internal.welcome-tour.step4.toggle-content'
                                    )}
                                    defaultChecked={true}
                                    onChange={(checked) => toggleVerifiedOnly(checked)}
                                />
                            </div>
                            {isErrorMessageDisplayed && (
                                <div className="welcome-tour-content-container">
                                    <Callout status="error" hasIcon={true}>
                                        <Text>
                                            <strong>{i18n.t('common:modal.error-occured.title')}</strong>
                                            <br />
                                            {i18n.t('common:modal.error-occured.content')}
                                        </Text>
                                    </Callout>
                                </div>
                            )}
                        </>
                    )}
                </Flex>
            </Modal>
        )
    );
};

CollectionOnboardingTour.propTypes = {
    user: PropTypes.instanceOf(User)
};

export default CollectionOnboardingTour;
